import { getBlockFixture, getPageFixture } from '@web-studio/core-fixtures';
import type { LayoutBlock } from '@web-studio/shared-schemas';
import { useCallback, useEffect, useState } from 'react';

function cloneBlocks(blocks: LayoutBlock[]): LayoutBlock[] {
  return structuredClone(blocks);
}

/** Session-local editable copy of a block fixture (resets when fixtureId changes). */
export function useBlockFixtureEditor(fixtureId: string) {
  const [blocks, setBlocks] = useState<LayoutBlock[]>([]);

  useEffect(() => {
    const fixture = getBlockFixture(fixtureId);
    setBlocks(fixture ? [cloneBlocks([fixture.block])[0]!] : []);
  }, [fixtureId]);

  const patchContent = useCallback((fieldKey: string, locale: string, value: string) => {
    setBlocks((prev) => {
      if (prev.length === 0) return prev;
      const block = { ...prev[0]! };
      const content = { ...block.content };
      const field = { ...(content[fieldKey] ?? {}), [locale]: value };
      content[fieldKey] = field;
      return [{ ...block, content }];
    });
  }, []);

  const patchMedia = useCallback((fieldKey: string, value: string) => {
    setBlocks((prev) => {
      if (prev.length === 0) return prev;
      const block = { ...prev[0]! };
      return [{ ...block, media: { ...block.media, [fieldKey]: value } }];
    });
  }, []);

  const reset = useCallback(() => {
    const fixture = getBlockFixture(fixtureId);
    setBlocks(fixture ? [cloneBlocks([fixture.block])[0]!] : []);
  }, [fixtureId]);

  return { blocks, patchContent, patchMedia, reset };
}

/** Session-local copy of a page fixture. */
export function usePageFixtureEditor(pageFixtureId: string) {
  const [blocks, setBlocks] = useState<LayoutBlock[]>(() => {
    const page = getPageFixture(pageFixtureId);
    return page ? cloneBlocks(page.blocks) : [];
  });

  useEffect(() => {
    const page = getPageFixture(pageFixtureId);
    setBlocks(page ? cloneBlocks(page.blocks) : []);
  }, [pageFixtureId]);

  const patchBlockContent = useCallback(
    (blockId: string, fieldKey: string, locale: string, value: string) => {
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.blockId !== blockId) return block;
          const content = { ...block.content };
          content[fieldKey] = { ...(content[fieldKey] ?? {}), [locale]: value };
          return { ...block, content };
        }),
      );
    },
    [],
  );

  const patchBlockMedia = useCallback((blockId: string, fieldKey: string, value: string) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.blockId !== blockId) return block;
        return { ...block, media: { ...block.media, [fieldKey]: value } };
      }),
    );
  }, []);

  const reset = useCallback(() => {
    const page = getPageFixture(pageFixtureId);
    setBlocks(page ? cloneBlocks(page.blocks) : []);
  }, [pageFixtureId]);

  return { blocks, patchBlockContent, patchBlockMedia, reset };
}

/**
 * Session-local editable blocks keyed by source (page fixture id or preview:vertical:tier).
 * Resets when `sourceKey` changes.
 */
export function useSeededBlocksEditor(sourceKey: string, seedBlocks: LayoutBlock[]) {
  const [blocks, setBlocks] = useState<LayoutBlock[]>(() => cloneBlocks(seedBlocks));
  const [activeKey, setActiveKey] = useState(sourceKey);

  if (activeKey !== sourceKey) {
    setActiveKey(sourceKey);
    setBlocks(cloneBlocks(seedBlocks));
  }

  const patchBlockContent = useCallback(
    (blockId: string, fieldKey: string, locale: string, value: string) => {
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.blockId !== blockId) return block;
          const content = { ...block.content };
          content[fieldKey] = { ...(content[fieldKey] ?? {}), [locale]: value };
          return { ...block, content };
        }),
      );
    },
    [],
  );

  const patchBlockMedia = useCallback((blockId: string, fieldKey: string, value: string) => {
    setBlocks((prev) =>
      prev.map((block) => {
        if (block.blockId !== blockId) return block;
        return { ...block, media: { ...block.media, [fieldKey]: value } };
      }),
    );
  }, []);

  const reset = useCallback(() => {
    setBlocks(cloneBlocks(seedBlocks));
  }, [seedBlocks]);

  return { blocks, patchBlockContent, patchBlockMedia, reset };
}
